import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { IconX, IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { format } from 'date-fns';
import useDeleteTrip from '../../hooks/useDeleteTrip';
import type { Trip } from '../../gql/graphql';

interface TripDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  trip: Trip | null;
  onSuccess?: () => void;
  bookingCount?: number;
}

const TripDeleteDialog: FC<TripDeleteDialogProps> = ({
  open,
  onClose,
  trip,
  onSuccess,
  bookingCount = 0,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const deleteTrip = useDeleteTrip();

  const handleClose = () => {
    if (!deleteTrip.isPending) {
      setConfirmText('');
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!trip || confirmText !== 'DELETE') {
      return;
    }

    try {
      const result = await deleteTrip.mutateAsync(trip.tripId);
      if (result.deleteTrip.success) {
        onSuccess?.();
        handleClose();
      } else {
        // Error message will be shown in the Alert component
        console.error('Delete failed:', result.deleteTrip.message);
      }
    } catch (error) {
      // Error is handled by the mutation and displayed in Alert
      console.error('Failed to delete trip:', error);
    }
  };

  const isConfirmValid = confirmText === 'DELETE';
  const hasBookings = bookingCount > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: '1rem' },
        },
      }}
    >
      <DialogTitle>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconTrash className="h-5 w-5 text-red-600" />
            <span className="text-lg font-semibold">Delete Trip</span>
          </div>
          <button
            onClick={handleClose}
            disabled={deleteTrip.isPending}
            className="text-gray-400 hover:text-gray-600"
          >
            <IconX className="h-5 w-5" />
          </button>
        </div>
      </DialogTitle>

      <DialogContent>
        {trip && (
          <div className="space-y-4">
            {/* Trip Details */}
            <Box className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <Typography variant="subtitle2" className="mb-2 font-semibold">
                Trip Details
              </Typography>
              <div className="space-y-1 text-sm">
                <div>
                  <span className="font-medium">Trip ID:</span> {trip.tripId}
                </div>
                <div>
                  <span className="font-medium">Boat:</span>{' '}
                  {trip.boatName || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Route:</span>{' '}
                  {trip.routeName || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Embarkation:</span>{' '}
                  {format(new Date(trip.embarkationDate), 'MMM dd, yyyy')}
                </div>
                <div>
                  <span className="font-medium">Disembarkation:</span>{' '}
                  {format(new Date(trip.disembarkationDate), 'MMM dd, yyyy')}
                </div>
              </div>
            </Box>

            {/* Booking Warning */}
            {hasBookings && (
              <Alert
                severity="warning"
                icon={<IconAlertTriangle className="h-5 w-5" />}
              >
                <Typography variant="body2" className="font-semibold">
                  This trip has {bookingCount} booking(s) associated with it.
                </Typography>
                <Typography variant="body2" className="mt-1">
                  Trips with bookings cannot be deleted. Please cancel or remove
                  all bookings before deleting this trip.
                </Typography>
              </Alert>
            )}

            {/* Error Message */}
            {(deleteTrip.error ||
              deleteTrip.data?.deleteTrip?.success === false) && (
              <Alert severity="error">
                <Typography variant="body2">
                  {(() => {
                    // First check if we have a response with error message
                    if (deleteTrip.data?.deleteTrip?.message) {
                      return deleteTrip.data.deleteTrip.message;
                    }
                    // Then check for GraphQL errors
                    const error = deleteTrip.error as {
                      response?: {
                        errors?: Array<{ message?: string }>;
                        data?: {
                          deleteTrip?: {
                            message?: string;
                          };
                        };
                      };
                    };
                    if (error?.response?.errors?.[0]?.message) {
                      return error.response.errors[0].message;
                    }
                    if (error?.response?.data?.deleteTrip?.message) {
                      return error.response.data.deleteTrip.message;
                    }
                    if (error instanceof Error) {
                      return error.message;
                    }
                    return 'An error occurred while deleting the trip';
                  })()}
                </Typography>
              </Alert>
            )}

            {/* Confirmation Input */}
            {!hasBookings && (
              <div className="space-y-2">
                <Typography variant="body2" className="text-gray-700">
                  To confirm deletion, please type{' '}
                  <span className="font-mono font-semibold text-red-600">
                    DELETE
                  </span>{' '}
                  in the field below:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  disabled={deleteTrip.isPending}
                  error={confirmText.length > 0 && !isConfirmValid}
                  helperText={
                    confirmText.length > 0 && !isConfirmValid
                      ? 'Please type DELETE exactly'
                      : ''
                  }
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className="gap-2 px-6 pb-4">
        <Button
          onClick={handleClose}
          disabled={deleteTrip.isPending}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={!isConfirmValid || deleteTrip.isPending || hasBookings}
          variant="contained"
          color="error"
          startIcon={
            deleteTrip.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <IconTrash className="h-4 w-4" />
            )
          }
        >
          {deleteTrip.isPending ? 'Deleting...' : 'Delete Trip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripDeleteDialog;
